import * as React from "react";
import styled from "@emotion/styled";
import {
  FlexRow,
  H1,
  H2,
  Link,
  LoadingSpinner,
  ScreenReaderText,
  SiteContainer,
  TagLink,
  Text,
  YouTubeEmbed,
} from "atoms";
import { ForbiddenModal, useAuth } from "features/auth";
import { FeaturedImage } from "features/images";
import { Head } from "features/misc";
import {
  ReviewList,
  ArtistReviewStats,
  ProjectReviewStats,
  ReviewSort,
  useReviewSort,
} from "features/reviews";
import { CreateTicket } from "features/tickets";
import { ApiError } from "utils/api";

import { ArtistCollections } from "./ArtistCollections";
import { AffiliatedCollections } from "./AffiliatedCollections";
import { ProjectBadges } from "./ProjectBadges";
import { ProjectSpecs } from "./ProjectSpecs";
import { ProjectTeam } from "./ProjectTeam";
import { RoadmapCategories } from "./RoadmapCategories";
import { SecondaryMenu } from "./SecondaryMenu";
import { SocialLink } from "./SocialLink";
import { getMarketplaceLink } from "../utils/getMarketplaceLink";
import { hasHugBadge } from "../utils/hasHugBadge";
import { Artist, Project } from "../types";

type ReviewableTemplateProps = {
  entity: Artist | Project;
  error: ApiError | null;
  isError?: boolean;
  isLoading?: boolean;
  // isFetching?: boolean;
  // isSuccess?: boolean;
  reviews: React.ComponentProps<typeof ReviewList>["reviews"];
  notFound: React.ReactNode;
};

function ReviewableTemplate({
  entity,
  error,
  isError,
  isLoading,
  reviews,
  notFound,
}: ReviewableTemplateProps) {
  const { activeReviewSort, createReviewSortHandler } = useReviewSort();

  const { user } = useAuth();
  const usersReview = React.useMemo(
    () => !!user && reviews?.find((r) => r.createdByUserId === user.id),
    [user, reviews]
  );

  const userHasUtilities = (user?.utilities?.length ?? 0) > 0;
  const isForbidden = error?.status === 401 || error?.status === 403;
  const entityType = entity.type.toLowerCase();

  // Content checks for common to all Reviewables
  const hasHighlights = !!entity?.highlights?.length;
  const hasRoadmapCategories =
    userHasUtilities && !!entity?.roadmapCategories?.length;
  const hasBadges = !!entity?.badges.length;
  const hasTags = !!(
    entity?.founderAttributes?.length || entity?.missionOrCause?.length
  );
  const showDisclaimer = hasHugBadge(entity);
  const marketplaceLink = getMarketplaceLink(entity.links);
  const reviewCountText = `${entity?.reviewCount ?? 0} Review${
    (entity?.reviewCount ?? 0) === 1 ? "" : "s"
  }`;

  // Overrides for each specific Reviewable type based on current entity
  const isArtist = entity.type === "ARTIST";
  const isProject = entity.type === "PROJECT";

  const hasArtistCollection = isArtist && !!entity.artistCollections.length;
  const hasProjectTeam = userHasUtilities && isProject && !!entity.team.length;
  const hasAffiliated = isProject && !!entity.affiliations.length;

  const [isReportFormOpen, setIsReportFormOpen] =
    React.useState<boolean>(false);
  const openReportForm = () => setIsReportFormOpen(true);
  const closeReportForm = () => setIsReportFormOpen(false);

  if (isLoading) {
    return (
      <SiteContainer width="md">
        <LoadingSpinner />
      </SiteContainer>
    );
  }

  if (!entity || (isError && !isForbidden)) {
    return (
      <SiteContainer width="md">
        {error?.status === 404
          ? notFound
          : "Sorry, we had a problem loading that resource."}
      </SiteContainer>
    );
  }

  return (
    <>
      <Head
        title={[
          entity.name,
          `${entityType[0].toUpperCase()}${entityType.slice(1)}s`,
        ]}
        description={entity.description?.slice(0, 200)}
        shareTitle={`View ${entity.name} on the HUG`}
        image={entity.heroImage.url600 ?? entity.heroImage.url}
        imageAlt={`Featured image for profile of ${entity.name}`}
        type="profile"
        username={entity.name}
      />

      <SiteContainer width="md">
        <ReviewableGrid
          entityType={entity.type}
          isForbidden={isForbidden}
          className={`entity_${entityType}`}
        >
          <ReviewableHeaderLeft>
            <FeaturedImage
              src={entity.heroImage}
              alt={`Featured NFT from ${entity.name} collection`}
            />
          </ReviewableHeaderLeft>
          <ReviewableHeaderRight>
            {entity.name && <EntityName lang="en">{entity.name}</EntityName>}

            <SecondaryMenu
              openReportForm={openReportForm}
              reviewable={entity}
            />

            <FlexRow>
              <ReviewsLink to="#reviews">{reviewCountText}</ReviewsLink>

              {!usersReview && (
                <ReviewsLink
                  to={`/${entity.type.toLowerCase()}s/${entity.id}/review`}
                >
                  {!entity.reviewCount
                    ? "Be the first to review"
                    : "Write a review"}
                </ReviewsLink>
              )}
            </FlexRow>

            {hasTags && (
              <TagList>
                {(entity?.founderAttributes || []).map((tag) => (
                  <TagLink tag={tag} variant="founderAttributes" key={tag} />
                ))}
                {(entity?.missionOrCause || []).map((tag) => (
                  <TagLink tag={tag} variant="missionOrCause" key={tag} />
                ))}
              </TagList>
            )}
          </ReviewableHeaderRight>

          <CreateTicketWrapper id="report-form">
            {isReportFormOpen && (
              <CreateTicket
                reviewableId={entity.id}
                reviewableType={entity.type}
                isOpen={isReportFormOpen}
                close={closeReportForm}
              />
            )}
          </CreateTicketWrapper>

          {isProject && <ProjectSpecs project={entity} />}

          <Links>
            <ScreenReaderText as="h2">Social links</ScreenReaderText>
            <LinkList>
              {entity?.links?.map((link) => (
                <li key={link.url}>
                  <SocialLink name={entity.name} link={link} />
                </li>
              ))}
            </LinkList>

            {marketplaceLink && (
              <MarketplaceLink
                variant="primary"
                size="sm"
                href={marketplaceLink.url}
              >
                Buy&nbsp;on {marketplaceLink.type[0].toUpperCase()}
                {marketplaceLink.type.slice(1)}
              </MarketplaceLink>
            )}
          </Links>

          <Description>
            {entity?.description && <Text size="xs">{entity.description}</Text>}

            {showDisclaimer && (
              <Disclaimer size="xxs">
                {entity?.name} is a client of HUG Studios, our advisory program
                which provides strategic guidance and mentorship
                to&nbsp;businesses.
              </Disclaimer>
            )}

            {hasHighlights && (
              <Highlights>
                <SH2 size="xs">Highlights</SH2>
                <HighlightsList>
                  {entity.highlights.map((h: string) => (
                    <Text size="xs" as="li" key={h}>
                      {h}
                    </Text>
                  ))}
                </HighlightsList>
              </Highlights>
            )}
          </Description>

          {entity?.youTubeVideo && (
            <Video className="YouTubeEmbed">
              <YouTubeEmbed embedId={entity?.youTubeVideo} />
            </Video>
          )}

          {isArtist && hasArtistCollection && (
            <RelatedCollections>
              <SH2 size="sm">Collections</SH2>
              <ArtistCollections collections={entity.artistCollections} />
            </RelatedCollections>
          )}

          {hasRoadmapCategories && (
            <Roadmap>
              <SH2 size="xs">Roadmap Categories</SH2>
              <RoadmapCategories categories={entity.roadmapCategories} />
            </Roadmap>
          )}

          {hasProjectTeam && (
            <Team>
              <SH2 size="xs">Team</SH2>
              <ProjectTeam team={entity.team} />
            </Team>
          )}

          {hasAffiliated && (
            <Affiliations>
              <SH2 size="xs">Affiliated Projects</SH2>
              <AffiliatedCollections affiliations={entity.affiliations} />
            </Affiliations>
          )}

          {hasBadges && (
            <Badges>
              <SH2 size="xs">Badges</SH2>
              <ProjectBadges badges={entity.badges} />
            </Badges>
          )}

          {userHasUtilities && (
            <Reviews>
              <ReviewsH2 size="h1" id="reviews">
                Reviews
              </ReviewsH2>

              {usersReview ? (
                <ReviewsLink
                  variant="primary"
                  to={`/${entityType}s/${entity.id}/reviews/${usersReview.type}/edit`}
                >
                  Edit your review
                </ReviewsLink>
              ) : (
                <ReviewsLink
                  variant="primary"
                  to={`/${entityType}s/${entity.id}/review`}
                >
                  Write a review
                </ReviewsLink>
              )}

              <ReviewColumns>
                <StatsColumn>
                  {entity.type === "ARTIST" && (
                    <ArtistReviewStats artistId={entity.id} />
                  )}
                  {entity.type === "PROJECT" && (
                    <ProjectReviewStats projectId={entity.id} />
                  )}
                </StatsColumn>

                <ReviewsColumn>
                  <FlexRow justifyContent="space-between" alignItems="center">
                    <strong>{reviewCountText}</strong>
                    <ReviewSort
                      activeOption={activeReviewSort}
                      createHandler={createReviewSortHandler}
                    />
                  </FlexRow>

                  <ReviewList
                    entityType={entity.type}
                    entityId={entity.id}
                    reviews={reviews}
                    sortBy={activeReviewSort}
                  />
                </ReviewsColumn>
              </ReviewColumns>
            </Reviews>
          )}
        </ReviewableGrid>
      </SiteContainer>
      <ForbiddenModal isShowing={isForbidden} />
    </>
  );
}

type WithIsForbidden = {
  isForbidden?: boolean;
  entityType: string;
};

const ReviewableGrid = styled.div<WithIsForbidden>(
  ({ theme, entityType, isForbidden = false }) => ({
    "&>*": {
      marginBottom: "3rem",
    },
    display: "grid",
    minHeight: "100vh",
    position: "relative",
    ...(entityType === "ARTIST" && {
      gridTemplateAreas: `
        "header-left"
        "header-right"
        "create-ticket"
        "links"
        "description"
        "video"
        "badges"
        "related-collections"
        "reviews"`,
    }),
    gridTemplateAreas: `
      "header-left"
      "header-right"
      "create-ticket"
      "project-specs"
      "links"
      "description"
      "video"
      "badges"
      "roadmap"
      "team"
      "related-collections"
      "reviews"`,
    "@media (min-width: 440px)": {
      gap: "0 1rem",
      gridTemplateColumns: "calc(40px + 10vw) 1fr",
      gridTemplateAreas: `
        "header-left header-right"
        "create-ticket create-ticket"
        "project-specs project-specs"
        "links links"
        "description description"
        "video video"
        "badges badges"
        "roadmap roadmap"
        "team team"
        "related-collections related-collections"
        "reviews reviews"`,
    },
    ...(entityType === "ARTIST" && {
      gridTemplateAreas: `
      "header-left header-right"
      "create-ticket create-ticket"
      "links links"
      "description description"
      "video video"
      "badges badges"
      "related-collections related-collections"
      "reviews reviews"`,
    }),
    [theme.breakpoints.desktop]: {
      "&>*": {
        marginBottom: "4rem",
      },
      gap: "0 3rem",
      gridTemplateColumns: "clamp(230px, 20vw, 275px) 1fr",
      gridTemplateAreas: `
        "header-left header-right"
        "create-ticket create-ticket"
        "project-specs project-specs"
        "links description"
        "video video"
        "badges roadmap"
        ". team"
        ". related-collections"
        "reviews reviews"
        `,
      ...(entityType === "ARTIST" && {
        gridTemplateAreas: `
        "header-left header-right"
        "create-ticket create-ticket"
        "links description"
        "video video"
        "badges related-collections"
        "reviews reviews"
        `,
      }),
    },
    ...(isForbidden
      ? {
          height: "50rem",
          overflow: "hidden",
          "&::after": {
            background: `linear-gradient(rgba(0,0,0,0), ${theme.colors.bgLight} 90%)`,
            bottom: 0,
            content: "''",
            display: "block",
            left: 0,
            position: "absolute",
            right: 0,
            top: 0,
            zIndex: 1,
          },
        }
      : {}),
  })
);

const ReviewableHeaderLeft = styled.div(({ theme }) => ({
  gridArea: "header-left",
  img: {
    borderRadius: theme.borderRadius.md,
    display: "block",
    maxWidth: 275,
    objectFit: "cover",
    width: "100%",
    height: "auto",
    aspectRatio: "1 / 1",
    margin: "0 auto",
  },
  ".entity_artist & img": {
    borderRadius: theme.borderRadius.round,
  },
}));

const ReviewableHeaderRight = styled.header({
  gridArea: "header-right",
  alignSelf: "center",
});

const EntityName = styled(H1)(({ theme }) => ({
  display: "inline-block",
  hyphens: "auto",
  lineHeight: "1.",
  wordBreak: "break-word",
  marginBottom: 10,
  "@media (max-width: 439px)": {
    fontSize: theme.fontSizes.h2,
  },
}));

const CreateTicketWrapper = styled.div({
  gridArea: "create-ticket",
  margin: "00 !important",
  "&>*": {
    margin: "0 0 5rem 0 !important",
  },
});

const TagList = styled.div({
  marginTop: 10,
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
});

const Links = styled.div({
  gridArea: "links",
});

const LinkList = styled.ul({
  gridArea: "links",
  display: "flex",
  flexDirection: "column",
  gap: ".5em",
  listStyleType: "none",
  margin: 0,
  padding: 0,
  lineHeight: 1.3,
  "li::before": {
    content: '"\\200B"',
    position: "absolute",
    height: 0,
  },
});

const MarketplaceLink = styled(Link)({
  marginTop: "1rem",
});

const Disclaimer = styled(Text)({
  marginTop: "2rem",
  marginRight: "2rem",
});

const Description = styled.div({
  gridArea: "description",
});

const Video = styled.div({
  gridArea: "video",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const Highlights = styled.div({
  gridArea: "highlights",
  marginTop: "1.5rem",
});

const Roadmap = styled.div({
  gridArea: "roadmap",
  maxWidth: "calc(100vw - 2rem)",
  ".YouTubeEmbed + &": {
    gridColumnStart: 1,
  },
});

const Badges = styled.div({
  gridArea: "badges",
});

const SH2 = styled(H2)(({ theme }) => ({
  marginBottom: 10,
  ...theme.fonts.body,
  fontWeight: "bold",
}));

const RelatedCollections = styled.div({
  gridArea: "related-collections",
  ".YouTubeEmbed + &": {
    gridColumnStart: 1,
  },
});

const Reviews = styled.div({
  gridArea: "reviews",
});

const ReviewColumns = styled.div(({ theme }) => ({
  [theme.breakpoints.desktop]: {
    display: "grid",
    gap: "3rem",
    gridTemplateColumns: "7fr 3fr",
    gridTemplateAreas: `"reviews stats"`,
  },
}));

const ReviewsColumn = styled.div({
  gridArea: "reviews",
});
const StatsColumn = styled.div({
  gridArea: "stats",
});

const HighlightsList = styled(Text)({
  li: {
    marginBottom: "0.66rem",
  },
}).withComponent("ul");

const ReviewsH2 = styled(H2)({
  paddingTop: "1.5rem",
  borderTop: "3px solid black",
});

const ReviewsLink = styled(Link)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: theme.fontSizes.xs,
  marginBottom: "1em",
}));

const Team = styled.div({
  gridArea: "team",
  ".YouTubeEmbed + &": {
    gridColumnStart: 1,
  },
});

const Affiliations = styled.div({
  gridArea: "related-collections",
});

export { ReviewableTemplate };
